<template>
<div class="overlay">
    <!--eslint-disable-next-line vuejs-accessibility/heading-has-content-->
    <h1 v-if="game?.wordmark?.data?.attributes?.url">
        <NuxtImg
            :src="`${game.wordmark.data.attributes.url}?format=auto`"
            :alt="game?.title || ''" class="wordmark"
        />
    </h1>
    <div class="tag-container" :class="{ 'no-game-type': !gameType }">
        <NuxtLink v-if="gameType" :to="typeLink">
            <Tag
                class="tag"
                :text="gameType.name"
                :icon="gameType.icon"
            />
        </NuxtLink>
        <Tag
            v-if="playerRange"
            class="tag"
            :text="`${playerRange} ${$t('TAGS.PLAYERS')}`"
            icon="fa fa-regular fa-user-group"
        />
        <Tag
            v-if="duration"
            class="tag"
            :text="`${duration} ${$t('TAGS.MINUTES')}`"
            icon="fa fa-regular fa-clock-eleven-thirty"
        />
    </div>
    <p>{{ game?.shortDescription }}</p>
    <button
        class="btn outline" v-on:click="scrollToPartOfSection"
        v-on:keydown.enter="scrollToPartOfSection"
    >
        <span>
            <i class="fa-sharp fa-light fa-arrow-down icon" />
        </span>
        <span class="text">Get it</span>
    </button>
</div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { JBGWeb } from '$types/JBGWeb'

export default defineNuxtComponent({
    props: {
        game: Object as PropType<JBGWeb.GameDetail>
    },
    computed: {
        gameType(): JBGWeb.GameType | null {
            if (!this.game?.features || !this.game?.features.types) return null
            return this.game?.features.types.data[0]?.attributes || null
        },
        playerRange(): string | null {
            if (!this.game?.features || !this.game?.features.counts) return null
            return `${this.game?.features.counts.playerMin} - ${this.game?.features.counts.playerMax}`
        },
        duration(): string | null {
            if (!this.game?.features || !this.game?.features.durationAvg) return null
            return this.game?.features.durationAvg.toString()
        },
        typeLink(): string {
            return this.localeRoute({
                name: 'game-type-gametypeslug',
                params: {
                    gametypeslug: this.gameType.slug
                }
            })
        }
    },
    methods: {
        scrollToPartOfSection() {
            const el = document.getElementById('part-of')
            if (!el) return
            el?.scrollIntoView({ behavior: 'smooth' })
        }
    }
})
</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;

.overlay {
    display: flex;
    flex-direction: column;
    background: rgba(0, 0, 0, 0.56);
    backdrop-filter: blur(20px);
    border-radius: 30px;
    padding: 40px;
    width: 516px;

    @include mq-small-and-more {
        margin: 80px 0px 0px;
    }

    @include mq-small {
        width: 360px;
        padding: 24px;
    }

    @include mq-xsmall {
        background-color: #111b26;
        box-shadow: none;
        backdrop-filter: none;
        border-radius: 0;
        padding: 0 12px;
        width: 100%;
    }

    h1 {
        margin: 0;
        text-align: center;
    }

    .wordmark {
        height: 180px;
        max-width: 100%;
        padding: 0px;
        margin-bottom: 24px;

        @include mq-small {
            height: 120px;
        }

        @include mq-xsmall {
            height: 110px;
            margin-bottom: 0;
        }
    }

    p {
        margin-bottom: 24px;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 6;
        -webkit-box-orient: vertical;
    }

    button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        height: 48px;
        border-radius: 8px;
    }
}

.tag-container {
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;
    flex-wrap: wrap;

    &.no-game-type {
        justify-content: flex-start;
        gap: 15px;
    }

    .tag {
        margin-right: 0px;
        margin-top: 0px;

        @include mq-medium {
            font-size: 16px;
            line-height: 16px;
            padding: 6px 16px;

            img, svg {
                height: 16px;
                margin-right: 12px;
                width: 16px;
            }
        }
    }
}

</style>
